/**
 * useTypewriterPlaceholder Hook
 *
 * Provides animated typewriter effect for placeholder text transitions.
 * When the placeholder changes, it animates:
 * 1. Backspace deletion of the old text (character by character)
 * 2. Typing of the new text (character by character)
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type TypewriterPhase = 'idle' | 'deleting' | 'typing';

interface UseTypewriterPlaceholderOptions {
  /** The target placeholder text */
  placeholder: string;
  /** Speed of deletion in ms per character (default: 20ms) */
  deleteSpeed?: number;
  /** Speed of typing in ms per character (default: 30ms) */
  typeSpeed?: number;
  /** Whether to animate the initial placeholder (default: false) */
  animateInitial?: boolean;
  /** Whether the animation is enabled (default: true) */
  enabled?: boolean;
}

interface UseTypewriterPlaceholderReturn {
  /** The currently displayed text */
  displayedText: string;
  /** Current animation phase */
  phase: TypewriterPhase;
  /** Whether animation is currently running */
  isAnimating: boolean;
}

export const useTypewriterPlaceholder = ({
  placeholder,
  deleteSpeed = 20,
  typeSpeed = 30,
  animateInitial = false,
  enabled = true,
}: UseTypewriterPlaceholderOptions): UseTypewriterPlaceholderReturn => {
  // Current displayed text
  const [displayedText, setDisplayedText] = useState(animateInitial ? '' : placeholder);
  // Current animation phase
  const [phase, setPhase] = useState<TypewriterPhase>('idle');

  // Track the previous placeholder for animations
  const prevPlaceholderRef = useRef(placeholder);
  // Target text we're animating towards
  const targetTextRef = useRef(placeholder);
  // Animation frame ID for cleanup
  const animationRef = useRef<number | null>(null);
  // Timeout ID for cleanup
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track if this is the initial mount
  const isInitialMount = useRef(true);

  // Cleanup function for animations
  const cleanup = useCallback(() => {
    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Handle placeholder changes
  useEffect(() => {
    // Skip animation on initial mount if not animating initial
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!animateInitial) {
        setDisplayedText(placeholder);
        prevPlaceholderRef.current = placeholder;
        return;
      }
      // For animateInitial, set prevPlaceholder to empty so we animate typing
      prevPlaceholderRef.current = '';
    }

    // If disabled or same placeholder, just update directly
    if (!enabled || placeholder === prevPlaceholderRef.current) {
      if (!enabled) {
        setDisplayedText(placeholder);
      }
      return;
    }

    // Store the target text
    targetTextRef.current = placeholder;
    const previousText = prevPlaceholderRef.current;
    prevPlaceholderRef.current = placeholder;

    // Clean up any existing animation
    cleanup();

    // Start deletion phase
    setPhase('deleting');
    let currentText = previousText;

    const deleteChar = () => {
      if (currentText.length > 0) {
        currentText = currentText.slice(0, -1);
        setDisplayedText(currentText);
        timeoutRef.current = setTimeout(deleteChar, deleteSpeed);
      } else {
        // Deletion complete, start typing phase
        setPhase('typing');
        typeChar();
      }
    };

    let typingIndex = 0;
    const targetText = placeholder;

    const typeChar = () => {
      if (typingIndex < targetText.length) {
        typingIndex++;
        setDisplayedText(targetText.slice(0, typingIndex));
        timeoutRef.current = setTimeout(typeChar, typeSpeed);
      } else {
        // Typing complete
        setPhase('idle');
      }
    };

    // Start the animation
    deleteChar();

    return cleanup;
  }, [placeholder, deleteSpeed, typeSpeed, animateInitial, enabled, cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    displayedText,
    phase,
    isAnimating: phase !== 'idle',
  };
};
